import EmptyState from '@app/src/components/EmptyState/EmptyState'
import type { Provider } from '@app/src/types/organizations'
import { InsightsOutlined } from '@mui/icons-material'
import { Stack } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { providerAssessmentLinkResolver } from './assessmentLinks'
import { useProfileBenchmark } from './benchmark/useProfileBenchmark'
import BenchmarkCompareFilter from './components/BenchmarkCompareFilter'
import IntegrityScreeningSection from './components/IntegrityScreeningSection'
import ProfileKpiCards from './components/ProfileKpiCards'
import ProfilePerformanceSections from './components/ProfilePerformanceSections'
import { buildAreaSections, computeOverallScore } from './profileLogic'
import { useProfileTopics } from './useProfileTopics'
import { useProviderAssessments } from './useProviderAssessments'
import { useProviderFindings } from './useProviderFindings'
import { useTopicRiskStatuses } from './useTopicRiskStatuses'

interface SustainabilityProfileSceneProps {
  provider: Provider
}

const SustainabilityProfileScene: React.FC<SustainabilityProfileSceneProps> = ({ provider }) => {
  const { formatMessage } = useIntl()
  const [showOverallRisk, setShowOverallRisk] = useState(true)
  const { sustainabilityProfile } = useFlags()

  const { topics, isLoading: isTopicsLoading } = useProfileTopics()
  const { scores, baseline, isLoading: isScoresLoading } = useProviderAssessments(provider.id)
  const { riskStatusByTemplate } = useTopicRiskStatuses(provider.id)
  const { screenings, totalFindings, isLoading: isFindingsLoading } = useProviderFindings(provider.id)

  const { peerGroup, setPeerGroup, benchmark, peerCount, isLoading: isBenchmarkLoading } = useProfileBenchmark(provider)

  const sections = useMemo(
    () => buildAreaSections(topics, scores, benchmark, { riskStatusByTemplate }),
    [topics, scores, benchmark, riskStatusByTemplate],
  )
  const overallScore = useMemo(() => computeOverallScore(sections), [sections])
  const resolveLink = useMemo(() => providerAssessmentLinkResolver(provider.id), [provider.id])

  const isScoresDataLoading = isTopicsLoading || isScoresLoading

  if (!sustainabilityProfile) return null

  return (
    <Stack py={3} spacing={3}>
      <BenchmarkCompareFilter
        provider={provider}
        peerGroup={peerGroup}
        peerCount={peerCount}
        isLoading={isBenchmarkLoading}
        onPeerGroupChange={setPeerGroup}
      />
      <ProfileKpiCards
        overallScore={overallScore}
        findingsTotal={totalFindings}
        benchmark={benchmark}
        peerGroup={peerGroup}
        isScoreLoading={isScoresDataLoading || isBenchmarkLoading}
        isFindingsLoading={isFindingsLoading || isBenchmarkLoading}
      />

      <ProfilePerformanceSections
        topics={topics}
        sections={sections}
        baseline={baseline}
        peerBaseline={benchmark?.baseline}
        peerGroup={peerGroup}
        isLoading={isScoresDataLoading}
        resolveLink={resolveLink}
        overallRisk={{ checked: showOverallRisk, onChange: setShowOverallRisk }}
        emptyState={
          <EmptyState
            iconComponent={InsightsOutlined}
            title={formatMessage({ id: 'sustainabilityProfile.empty.title' })}
            description={formatMessage({ id: 'sustainabilityProfile.empty.description' })}
          />
        }
      />

      <IntegrityScreeningSection
        providerId={provider.id}
        screenings={screenings}
        benchmark={benchmark}
        isLoading={isFindingsLoading}
      />
    </Stack>
  )
}

export default SustainabilityProfileScene
